import React, { useState, useRef, useEffect, useCallback } from "react";
import { PlxsTag, PlxsCallout, PlxsIcon } from "@plexlogic/plexus-ui";
import { Prompt } from "react-router-dom";
import DrawSetupStep from "./drawWizardSteps/DrawSetupStep";
import EntryMethodStep from "./drawWizardSteps/EntryMethodStep";
import { EntryConfigurationStep } from "./uploadEntries";
import PrizeConfigurationStep from "./drawWizardSteps/PrizeConfigurationStep";
import ReviewStep from "./drawWizardSteps/ReviewStep";
import SchedulingStep from "./drawWizardSteps/SchedulingStep";
import PaymentStep from "./drawWizardSteps/PaymentStep";
import ExecutionConfirmation from "./drawWizardSteps/ExecutionConfirmationStep";
import ScheduledConfirmationStep from "./drawWizardSteps/ScheduledConfirmationStep";
import ScheduledLockBanner from "./ScheduledLockBanner";
import { DrawData } from "../types/DrawData";
import {
  WIZARD_STYLES,
  getStepStyle,
  getStepHoverStyle,
  getStepLeaveStyle,
} from "./wizardStyles";
import { toSentenceCase } from "../utils/stringUtils";
import {
  getWizardCardHeader,
  buildStepperItems,
} from "../utils/wizardStepDisplay";
import useUploadStore from "./uploadEntries/useUploadStore";
import apiService, {
  setClaimSessionExpiredFunction,
  SubmitDrawRequest,
  DrawResponse,
} from "../services/api";
import {
  getClaimToken,
  clearClaimToken,
  getStoredDrawIds,
  hasWizardSession,
  getWizardSession,
  clearWizardSession,
  setActiveDrawId,
  getActiveDrawId,
  clearActiveDrawId,
} from "../services/wizardStorage";
import { useAuth } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import config from "../config";

const buildSaveRequest = (
  drawData: DrawData,
  csvSettings: ReturnType<
    typeof useUploadStore.getState
  >["csvConfirmationSettings"],
  wizardStep: number
): SubmitDrawRequest => ({
  draw_name: drawData.drawName,
  organisation: drawData.organisation,
  entry_method: drawData.entryMethod,
  entry_count: drawData.entryCount,
  prizes: drawData.prizes.map((prize) => ({
    description: prize.description,
    winners: prize.winners,
    reserves: prize.reserves || 0,
    estimated_value: prize.estimatedValue || 0,
  })),
  csv_confirmation_setting: csvSettings
    ? {
        hasHeader: csvSettings.isHeaderRow,
        ticketCountColumn: csvSettings.ticketCountColumn,
        participantIdentificationColumn:
          csvSettings.participantIdentificationColumn,
      }
    : undefined,
  total_prize_pool_value: drawData.totalPrizePoolValue,
  scrutineer_settings:
    drawData.scrutineerSettings?.scrutineerRequired !== undefined
      ? {
          scrutineer_required: drawData.scrutineerSettings.scrutineerRequired,
          scrutineer_name: drawData.scrutineerSettings.scrutineerName,
          scrutineer_email: drawData.scrutineerSettings.scrutineerEmail,
          scrutineer_job_title: drawData.scrutineerSettings.scrutineerJobTitle,
          scrutineer_confirmation:
            drawData.scrutineerSettings.scrutineerConfirmation,
        }
      : undefined,
  max_prizes_per_entrant: drawData.maxPrizesPerEntrant,
  scheduled_date_time: drawData.scheduledDateTime?.toISOString(),
  wizard_step: wizardStep,
});

const drawResponseToDrawData = (
  response: DrawResponse,
  isDemo: boolean
): DrawData => ({
  drawId: response.draw_id,
  drawName: response.draw_name,
  organisation: response.organisation,
  drawNo: response.draw_no,
  entryMethod:
    (response.entry_method as DrawData["entryMethod"]) || "simple",
  entryCount: response.entry_count,
  prizes:
    response.prizes && response.prizes.length > 0
      ? response.prizes.map((p) => ({
          description: p.description,
          winners: p.winners,
          reserves: p.reserves,
          estimatedValue: p.estimated_value ?? 0,
        }))
      : [{ description: "", winners: 1, reserves: 2, estimatedValue: 0 }],
  isDemo,
  totalPrizePoolValue: response.total_prize_pool_value ?? 0,
  scrutineerSettings: response.scrutineer_settings
    ? {
        scrutineerRequired: response.scrutineer_settings.scrutineer_required,
        scrutineerName: response.scrutineer_settings.scrutineer_name,
        scrutineerEmail: response.scrutineer_settings.scrutineer_email,
        scrutineerJobTitle: response.scrutineer_settings.scrutineer_job_title,
        scrutineerConfirmation:
          response.scrutineer_settings.scrutineer_confirmation,
      }
    : { scrutineerRequired: undefined },
  maxPrizesPerEntrant: response.max_prizes_per_entrant,
  scheduledDateTime: response.scheduled_date_time
    ? new Date(response.scheduled_date_time)
    : undefined,
});

const DrawWizard: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { showNotification } = useNotification();
  const [currentStep, setCurrentStep] = useState(0);
  const isDemo =
    new URLSearchParams(window.location.search).get("demo") === "true";
  const [drawId, setDrawId] = useState<string>("");
  const [paymentRequired, setPaymentRequired] = useState<boolean>(true); // Default to requiring payment

  // Determine payment requirement from localStorage (avoid useAuth dependency)
  useEffect(() => {
    const isExternal = localStorage.getItem("isExternalUser");
    // External users need to pay, internal Plexus users don't
    // Default to true for anonymous users (isExternal not yet set)
    setPaymentRequired(isExternal !== "false");
  }, [isAuthenticated]); // Re-evaluate when auth state changes

  // Track if draw is completed to prevent navigation warnings
  const [isDrawCompleted, setIsDrawCompleted] = useState(false);

  const [isLocked, setIsLocked] = useState(false);

  // Access upload store to set draw ID
  const setCurrentDrawId = useUploadStore((state) => state.setCurrentDrawId);
  const hydrateUploadStoreFromDraw = useUploadStore(
    (state) => state.hydrateFromDraw
  );

  const drawIdRef = useRef<string>("");

  const [drawData, setDrawData] = useState<DrawData>({
    drawName: "",
    organisation: "",
    entryMethod: "simple",
    prizes: [{ description: "", winners: 1, reserves: 2, estimatedValue: 0 }],
    isDemo,
    totalPrizePoolValue: 0,
    scrutineerSettings: {
      scrutineerRequired: undefined,
    },
    maxPrizesPerEntrant: "one",
  });

  const isScheduledAnonymous =
    !isAuthenticated && !!drawData.scheduledDateTime;
  const stepNames = [
    "SETUP",
    "ENTRY METHOD",
    "ENTRY CONFIGURATION",
    "PRIZES",
    "SCHEDULING",
    "REVIEW",
    ...(isScheduledAnonymous
      ? ["SCHEDULED"]
      : ["EXECUTE", ...(paymentRequired ? ["PAYMENT", "RESULTS"] : [])]),
  ].map(toSentenceCase);

  const stepperItems = buildStepperItems(stepNames);
  const activeStepperIndex = stepperItems.findIndex(
    (item) => currentStep >= item.firstStep && currentStep <= item.lastStep
  );

  const drawDataRef = useRef<DrawData>(drawData);
  const writeDrawData = useCallback(
    (next: DrawData | ((prev: DrawData) => DrawData)) => {
      const resolved =
        typeof next === "function"
          ? (next as (prev: DrawData) => DrawData)(drawDataRef.current)
          : next;
      drawDataRef.current = resolved;
      setDrawData(resolved);
    },
    []
  );

  const [prefetchedResult, setPrefetchedResult] =
    useState<DrawResponse | null>(null);

  const landOnResults = useCallback(
    (full: DrawResponse, opts?: { goDirectToResults?: boolean }) => {
      const restored = drawResponseToDrawData(full, isDemo);
      const goDirect = opts?.goDirectToResults === true;
      const paid = goDirect ? true : full.payment_required === false;
      writeDrawData({
        ...restored,
        paymentStatus: paid ? "paid" : "unpaid",
      });
      setDrawId(full.draw_id);
      drawIdRef.current = full.draw_id;
      setIsDrawCompleted(true);
      setIsLocked(true);
      const stepNamesForLanding = [
        "SETUP",
        "ENTRY METHOD",
        "ENTRY CONFIGURATION",
        "PRIZES",
        "SCHEDULING",
        "REVIEW",
        "EXECUTE",
        ...(paymentRequired ? ["PAYMENT", "RESULTS"] : []),
      ].map(toSentenceCase);
      const paymentIdx = stepNamesForLanding.indexOf(
        toSentenceCase("PAYMENT")
      );
      const executeIdx = stepNamesForLanding.indexOf(
        toSentenceCase("EXECUTE")
      );
      const resultsIdx = stepNamesForLanding.indexOf(
        toSentenceCase("RESULTS")
      );
      if (goDirect) {
        setPrefetchedResult(full);
        setCurrentStep(resultsIdx >= 0 ? resultsIdx : Math.max(executeIdx, 0));
        return;
      }
      if (!isAuthenticated && config.features.anonymousDraftsEnabled) {
        setPrefetchedResult(full);
        setCurrentStep(Math.max(executeIdx, 0));
        return;
      }
      if (paymentIdx >= 0) {
        setPrefetchedResult(null);
        setCurrentStep(paymentIdx);
      } else {
        setPrefetchedResult(paid ? full : null);
        setCurrentStep(Math.max(executeIdx, 0));
      }
    },
    [isAuthenticated, isDemo, paymentRequired, writeDrawData]
  );

  const hydrateUploadStoreFromResponse = useCallback(
    async (response: DrawResponse): Promise<void> => {
      const file = response.files?.[0];
      if (!file?.file_name) {
        setCurrentDrawId(response.draw_id);
        return;
      }
      try {
        const meta = await apiService.getEntryMetadata(
          response.draw_id,
          file.file_name
        );
        if (!isMountedRef.current) return;
        const csvSetting = response.csv_confirmation_setting;
        const ticketCountColumn = csvSetting?.ticketCountColumn;
        const participantIdentificationColumn =
          csvSetting?.participantIdentificationColumn;
        const fileStructure = ticketCountColumn
          ? "count-per-entrant"
          : "one-entry-per-row";
        hydrateUploadStoreFromDraw(
          response.draw_id,
          {
            originalFileName: meta.originalFileName,
            storedFileName: meta.storedFileName,
            size: meta.size,
            mimeType: meta.mimeType,
            uploadedAt: meta.uploadedAt,
            status: meta.status,
            rowCount: meta.csvPreviewData?.rowCount,
            headerRowColumns: meta.csvPreviewData?.headerRowColumns,
            entryRow1Columns: meta.csvPreviewData?.entryRow1Columns,
            entryRow2Columns: meta.csvPreviewData?.entryRow2Columns,
          },
          {
            isHeaderRow: csvSetting?.hasHeader,
            fileStructure,
            ticketCountColumn,
            participantIdentificationColumn,
            uniqueIdColumn:
              fileStructure === "one-entry-per-row"
                ? participantIdentificationColumn
                : undefined,
          }
        );
      } catch {
        setCurrentDrawId(response.draw_id);
      }
    },
    [hydrateUploadStoreFromDraw, setCurrentDrawId]
  );

  const rehydrateFromDraftId = useCallback(
    async (
      candidateDrawId: string,
      onFailure?: () => void
    ): Promise<boolean> => {
      try {
        const response = await apiService.getDrawById(candidateDrawId);
        if (!isMountedRef.current) return false;
        if (response.status !== "draft") {
          onFailure?.();
          if (response.status === "completed") {
            clearWizardSession();
            if (!isAuthenticated && response.scheduled_date_time) {
              clearActiveDrawId();
              window.location.href = `/draws/${response.draw_id}`;
              return false;
            }
            await hydrateUploadStoreFromResponse(response);
            landOnResults(response);
            return false;
          }
          if (response.status === "scheduled" && !isAuthenticated) {
            const restored = drawResponseToDrawData(response, isDemo);
            drawIdRef.current = response.draw_id;
            setDrawId(response.draw_id);
            writeDrawData(restored);
            setIsLocked(true);
            await hydrateUploadStoreFromResponse(response);
            const stepNamesForScheduled = [
              "SETUP",
              "ENTRY METHOD",
              "ENTRY CONFIGURATION",
              "PRIZES", 
              "SCHEDULING",
              "REVIEW",
              "SCHEDULED",
            ].map(toSentenceCase);
            const scheduledIdx = stepNamesForScheduled.indexOf(
              toSentenceCase("SCHEDULED")
            );
            setCurrentStep(scheduledIdx);
            return true;
          }
          clearActiveDrawId();
          clearWizardSession();
          window.location.href = `/draws/${response.draw_id}`;
          return false;
        }

        const restored = drawResponseToDrawData(response, isDemo);
        drawIdRef.current = response.draw_id;
        setDrawId(response.draw_id);
        writeDrawData(restored);

        await hydrateUploadStoreFromResponse(response);

        if (typeof response.wizard_step === "number") {
          setCurrentStep(response.wizard_step);
        }
        return true;
      } catch {
        onFailure?.();
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDemo, landOnResults]
  );

  useEffect(() => {
    if (!config.features.anonymousDraftsEnabled) return;
    if (isAuthenticated || drawId) return;

    const candidates = getStoredDrawIds();
    if (candidates.length === 0) return;

    rehydrateFromDraftId(candidates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthenticated || drawId) return;
    if (hasWizardSession()) return;
    const activeDrawId = getActiveDrawId();
    if (!activeDrawId) return;

    rehydrateFromDraftId(activeDrawId, () => {
      clearActiveDrawId();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const [isRestoringSession, setIsRestoringSession] = useState(false);

  useEffect(() => {
    const session = getWizardSession();
    if (!session || !isAuthenticated) return;
    if (restoreInFlightRef.current) return;
    restoreInFlightRef.current = true;

    const parsed: any = session.data;
    if (parsed.scheduledDateTime) {
      parsed.scheduledDateTime = new Date(parsed.scheduledDateTime);
    }
    writeDrawData(parsed);
    clearWizardSession();
    setIsRestoringSession(true);

    const targetStep = session.step;

    const persistedDrawId: string | undefined = parsed.drawId;
    const storedToken = persistedDrawId
      ? getClaimToken(persistedDrawId)
      : null;
    const shouldClaim =
      config.features.anonymousDraftsEnabled &&
      !!persistedDrawId &&
      !!storedToken;

    if (shouldClaim && persistedDrawId && storedToken) {
      const claimAndProceed = async () => {
        try {
          const response = await apiService.claimDraw(
            persistedDrawId,
            storedToken
          );
          clearClaimToken(persistedDrawId);
          if (response.status && response.status !== "draft") {
            clearActiveDrawId();
            if (response.status === "scheduled") {
              window.location.href = `/draws/${response.draw_id}`;
              return;
            }
            try {
              const fullDraw = await apiService.getDrawById(
                response.draw_id
              );
              if (!isMountedRef.current) return;
              landOnResults(fullDraw);
            } catch {
              window.location.href = `/draws/${response.draw_id}`;
            }
            return;
          }
          setActiveDrawId(response.draw_id);
          await rehydrateFromDraftId(response.draw_id);
        } catch {
          // Claim failed (network/auth race); leave the token for a later
          // retry rather than blocking the wizard.
        } finally {
          setIsRestoringSession(false);
          restoreInFlightRef.current = false;
        }
      };
      claimAndProceed();
      return;
    }

    const createDraftAndProceed = async () => {
      try {
        if (persistedDrawId) {
          try {
            const existing = await apiService.getDrawById(persistedDrawId);
            if (existing.status === "scheduled") {
              clearActiveDrawId();
              window.location.href = `/draws/${existing.draw_id}`;
              return;
            }
            if (existing.status === "completed") {
              clearActiveDrawId();
              landOnResults(existing);
              return;
            }
            setActiveDrawId(existing.draw_id);
            await rehydrateFromDraftId(existing.draw_id);
            return;
          } catch {
          }
        }
        const response = await apiService.createDraw({
          draw_name: parsed.drawName,
          organisation: parsed.organisation,
        });
        setDrawId(response.draw_id);
        setActiveDrawId(response.draw_id);
        setCurrentDrawId(response.draw_id);
        writeDrawData((prev) => ({
          ...prev,
          drawId: response.draw_id,
          drawNo: response.draw_no,
        }));
        setCurrentStep(targetStep);
      } catch (error) {
        showNotification(
          "RESTORE_FAILED",
          "Couldn't restore your draw. Please try again."
        );
      } finally {
        setIsRestoringSession(false);
        restoreInFlightRef.current = false;
      }
    };
    createDraftAndProceed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Scroll functionality for step navigation
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Ref to track component mount status to prevent memory leaks
  const isMountedRef = useRef(true);

  // Guards the session-restore effect against concurrent runs (a re-render
  // mid-flight must not kick off a second claim/create).
  const restoreInFlightRef = useRef(false);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!config.features.anonymousDraftsEnabled) return;
    setClaimSessionExpiredFunction(() => {
      if (!isMountedRef.current) return;
      setIsRestoringSession(false);
      setDrawId("");
      clearActiveDrawId();
      setCurrentDrawId("");
      setCurrentStep(0);
      writeDrawData({
        drawName: "",
        organisation: "",
        entryMethod: "simple",
        prizes: [
          { description: "", winners: 1, reserves: 2, estimatedValue: 0 },
        ],
        isDemo,
        totalPrizePoolValue: 0,
        scrutineerSettings: { scrutineerRequired: undefined },
        maxPrizesPerEntrant: "one",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warn user before leaving page if draw is in progress (draft exists and not completed)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Skip warning if redirecting to login (automatic, not user-initiated)
      const isRedirecting = sessionStorage.getItem('isRedirectingToLogin');
      if (isRedirecting) {
        return;
      }
      
      // Only show warning if a draw has been created (draft exists) and hasn't been completed
      if (drawId && !isDrawCompleted) {
        e.preventDefault();
        e.returnValue = ""; // Chrome requires returnValue to be set
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [drawId, isDrawCompleted]);

  // Check scroll position and update indicators
  const checkScrollPosition = () => {
    if (scrollContainerRef.current && isMountedRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      // Only show right arrow if there's actually content to scroll to
      setCanScrollRight(
        scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 5
      );
    }
  };

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  // Check scroll position on mount and resize
  useEffect(() => {
    checkScrollPosition();
    const handleResize = () => checkScrollPosition();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check scroll position when current step changes
  useEffect(() => {
    const scrollCurrentStepIntoView = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const stepElements = container.children;
        const currentStepElement = stepElements[activeStepperIndex] as HTMLElement;

        if (currentStepElement) {
          const containerRect = container.getBoundingClientRect();
          const stepRect = currentStepElement.getBoundingClientRect();

          // Check if step is fully visible
          const isFullyVisible =
            stepRect.left >= containerRect.left &&
            stepRect.right <= containerRect.right;

          if (!isFullyVisible) {
            // Calculate scroll position to center the step
            const stepCenter =
              currentStepElement.offsetLeft +
              currentStepElement.offsetWidth / 2;
            const containerCenter = container.offsetWidth / 2;
            const targetScrollLeft = stepCenter - containerCenter;

            container.scrollTo({
              left: Math.max(0, targetScrollLeft),
              behavior: "smooth",
            });
          }
        }
      }
    };

    const scrollTimer = setTimeout(() => {
      if (isMountedRef.current) {
        checkScrollPosition();
        scrollCurrentStepIntoView();
      }
    }, 100); // Small delay to ensure DOM is updated

    return () => clearTimeout(scrollTimer);
  }, [currentStep, activeStepperIndex]);

  const handleNext = async () => {
    // Only check session for authenticated users with an existing draw
    if (drawId && isAuthenticated) {
      try {
        // Makes lightweight authenticated API call to /api/draws/session
        // Interceptor handles: 401 → refresh token → retry → success OR redirect to login
        await apiService.checkSession();
      } catch (error) {
        // If interceptor couldn't refresh token, user will be redirected to login
        return;
      }
    }

    const nextStep = currentStep + 1;

    const effectiveDrawId = drawIdRef.current || drawId;
    if (effectiveDrawId && !isLocked) {
      try {
        const csvSettings = useUploadStore.getState().csvConfirmationSettings;
        await apiService.updateDraw(
          effectiveDrawId,
          buildSaveRequest(drawDataRef.current, csvSettings, nextStep)
        );
      } catch (error) {
        showNotification(
          "AUTOSAVE_FAILED",
          "Couldn't save your progress. Please try again."
        );
        return;
      }
    }

    if (nextStep < stepNames.length) {
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    const prevStep = currentStep - 1;
    if (prevStep >= 0) {
      setCurrentStep(prevStep);
    }
  };

  const updateDrawData = (updates: Partial<DrawData>) => {
    writeDrawData((prev) => ({ ...prev, ...updates }));
  };

  const handleDrawCreated = (drawId: string, drawNo?: string) => {
    drawIdRef.current = drawId;
    setDrawId(drawId);
    setActiveDrawId(drawId);
    // Set the drawId in the upload store so all uploads use this draw
    setCurrentDrawId(drawId);
    if (drawNo) {
      updateDrawData({ drawNo });
    }
  };

  const handleNewDraw = () => {
    // Reset all state to start a new draw
    setCurrentStep(0);
    setDrawId("");
    clearActiveDrawId();

    // Reset upload store completely (this will trigger full reset via setCurrentDrawId)
    setCurrentDrawId("");

    setIsDrawCompleted(false); // Reset completion status
    setIsLocked(false);
    setPrefetchedResult(null);
    writeDrawData({
      drawName: "",
      organisation: "",
      entryMethod: "simple",
      prizes: [{ description: "", winners: 1, reserves: 2, estimatedValue: 0 }],
      isDemo,
      totalPrizePoolValue: 0,
      scrutineerSettings: {
        scrutineerRequired: undefined,
      },
      maxPrizesPerEntrant: "one",
    });
    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDrawExecuted = useCallback((result?: DrawResponse) => {
    // Mark draw as completed to prevent navigation warnings
    setIsDrawCompleted(true);
    setIsLocked(true);
    if (result) {
      setPrefetchedResult(result);
    }
    clearActiveDrawId();
  }, []);

  const renderStepContent = (step: number) => {
    const name = stepNames[step];
    switch (name) {
      case toSentenceCase("SETUP"):
        return (
          <DrawSetupStep
            drawData={drawData}
            updateDrawData={updateDrawData}
            onNext={handleNext}
            onDrawCreated={handleDrawCreated}
            isLocked={isLocked}
          />
        );
      case toSentenceCase("ENTRY METHOD"):
        return (
          <EntryMethodStep
            drawData={drawData}
            updateDrawData={updateDrawData}
            onNext={handleNext}
            onBack={handleBack}
            isLocked={isLocked}
          />
        );
      case toSentenceCase("ENTRY CONFIGURATION"):
        return (
          <EntryConfigurationStep
            drawData={drawData}
            updateDrawData={updateDrawData}
            onNext={handleNext}
            onBack={handleBack}
            isLocked={isLocked}
          />
        );
      case toSentenceCase("PRIZES"):
        return (
          <PrizeConfigurationStep
            drawData={drawData}
            updateDrawData={updateDrawData}
            onNext={handleNext}
            onBack={handleBack}
            isLocked={isLocked}
          />
        );
      case toSentenceCase("SCHEDULING"):
        return (
          <SchedulingStep
            drawData={drawData}
            updateDrawData={updateDrawData}
            onNext={handleNext}
            onBack={handleBack}
            isLocked={isLocked}
          />
        );
      case toSentenceCase("REVIEW"):
        return (
          <ReviewStep
            drawData={drawData}
            drawId={drawId}
            onNext={handleNext}
            onDrawCreated={handleDrawCreated}
            onBack={handleBack}
            updateDrawData={updateDrawData}
            isLocked={isLocked}
          />
        );
      case toSentenceCase("SCHEDULED"):
        return (
          <ScheduledConfirmationStep
            drawData={drawData}
            currentStep={step}
            onLocked={() => setIsLocked(true)}
          />
        );
      case toSentenceCase("PAYMENT"):
        return (
          <PaymentStep
            drawData={drawData}
            onNext={async (updates) => {
              if (updates) {
                updateDrawData(updates);
              }
              const targetId = drawIdRef.current || drawId;
              if (!targetId) return;
              try {
                const fullDraw = await apiService.getDrawById(targetId);
                clearActiveDrawId();
                clearWizardSession();
                landOnResults(fullDraw, { goDirectToResults: true });
              } catch {
                clearActiveDrawId();
                clearWizardSession();
                window.location.href = `/draws/${targetId}`;
              }
            }}
            onBack={handleBack}
          />
        );
      case toSentenceCase("EXECUTE"):
        return (
          <ExecutionConfirmation
            drawData={drawData}
            drawId={drawId}
            currentStep={step}
            paymentRequired={paymentRequired}
            onNewDraw={handleNewDraw}
            onDrawExecuted={handleDrawExecuted}
            onNext={handleNext}
            prefetchedResult={prefetchedResult}
          />
        );
      case toSentenceCase("RESULTS"):
        return (
          <ExecutionConfirmation
            drawData={drawData}
            drawId={drawId}
            currentStep={step}
            paymentRequired={paymentRequired}
            onNewDraw={handleNewDraw}
            onDrawExecuted={handleDrawExecuted}
            prefetchedResult={prefetchedResult}
          />
        );
      default:
        return null;
    }
  };

  // Show loading while restoring session after auth redirect
  if (isRestoringSession) {
    return (
      <div
        style={WIZARD_STYLES.wizardContainer}
        className="wizard-container-responsive"
        data-testid="draw-wizard-page"
      >
        <div style={WIZARD_STYLES.contentCard}>
          <div style={{ padding: "var(--plexus-space-xl)", textAlign: "center" }}>
            <PlxsCallout
              dataTestId="restoring-session-callout"
              tone="brand"
              message="Restoring your draw progress..."
            />
          </div>
        </div>
      </div>
    );
  }

  const showScheduledBanner =
    isLocked && !isDrawCompleted && !!drawData.scheduledDateTime;

  return (
    <div
      style={WIZARD_STYLES.wizardContainer}
      className={`wizard-container-responsive${
        showScheduledBanner ? " has-scheduled-banner" : ""
      }`}
      data-testid="draw-wizard-page"
    >
      {/* Navigation warning for in-app routing */}
      <Prompt
        when={!!drawId && !isDrawCompleted && !sessionStorage.getItem('isRedirectingToLogin')}
        message="You have unsaved changes. Are you sure you want to leave? All progress will be lost."
      />

      {showScheduledBanner && (
        <ScheduledLockBanner scheduledDateTime={drawData.scheduledDateTime} />
      )}

      {/* Demo Alert */}
      {isDemo && (
        <div style={WIZARD_STYLES.demoContainer}>
          <PlxsCallout
            dataTestId="demo-callout"
            tone="brand"
            message={
              "Demo mode: This is a demonstration. No actual random draws will be performed."
            }
          />
        </div>
      )}

      {/* Main Content Card */}
      <div style={WIZARD_STYLES.contentCard}>
        {/* Header */}
        <div style={WIZARD_STYLES.header}>
          <div style={WIZARD_STYLES.headerContent}>
            <div style={WIZARD_STYLES.headerTitle}>
              {getWizardCardHeader(stepNames[currentStep])}
            </div>
            {isDemo && <PlxsTag label="Demo Mode" />}
          </div>
        </div>

        {/* Step Content */}
        <div style={WIZARD_STYLES.stepContent}>
          {(() => {
            const stepName = stepNames[currentStep];
            const UNLOCKED_TERMINAL_STEPS = [
              toSentenceCase("SCHEDULED"),
              toSentenceCase("PAYMENT"),
              toSentenceCase("EXECUTE"),
              toSentenceCase("RESULTS"),
            ];
            const lockClass =
              isLocked && !UNLOCKED_TERMINAL_STEPS.includes(stepName)
                ? "wizard-step-locked"
                : undefined;
            return (
              <div
                key={currentStep}
                style={WIZARD_STYLES.stepTransition}
                className={lockClass}
              >
                {renderStepContent(currentStep)}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Fixed Bottom Navigation */}
      <div style={WIZARD_STYLES.fixedBottomNav}>
        <div style={WIZARD_STYLES.bottomNavContent}>
          {/* Progress Steps */}
          <div style={WIZARD_STYLES.progressContainer}>
            {/* Left scroll indicator */}
            <div
              style={{
                ...WIZARD_STYLES.scrollIndicator,
                ...WIZARD_STYLES.scrollIndicatorLeft,
                ...(canScrollLeft ? {} : WIZARD_STYLES.scrollIndicatorHidden),
              }}
              onClick={scrollLeft}
            >
              <PlxsIcon name="chevronLeft" size="sm" />
            </div>

            {/* Scrollable steps container */}
            <div
              ref={scrollContainerRef}
              className="wizard-scroll-container"
              style={WIZARD_STYLES.progressScrollContainer}
              onScroll={checkScrollPosition}
            >
              {stepperItems.map((item) => {
                const isActive =
                  currentStep >= item.firstStep &&
                  currentStep <= item.lastStep;
                const isCompleted = currentStep > item.lastStep;
                const isClickable = item.firstStep <= currentStep;

                return (
                  <div
                    key={item.label}
                    onClick={() => {
                      if (isClickable) {
                        setCurrentStep(item.firstStep);
                      }
                    }}
                    style={getStepStyle(
                      isActive,
                      isCompleted,
                      isClickable,
                      true
                    )}
                    onMouseEnter={(e) => {
                      if (isClickable) {
                        Object.assign(
                          e.currentTarget.style,
                          getStepHoverStyle(isActive, isCompleted)
                        );
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isClickable) {
                        Object.assign(
                          e.currentTarget.style,
                          getStepLeaveStyle(isActive, isCompleted)
                        );
                      }
                    }}
                  >
                    {item.label}
                  </div>
                );
              })}
            </div>

            {/* Right scroll indicator */}
            <div
              style={{
                ...WIZARD_STYLES.scrollIndicator,
                ...WIZARD_STYLES.scrollIndicatorRight,
                ...(canScrollRight ? {} : WIZARD_STYLES.scrollIndicatorHidden),
              }}
              onClick={scrollRight}
            >
              <PlxsIcon name="chevronRight" size="sm" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DrawWizard;
